// Lógica de presentación de "Mi plan" (§3.3 de suscripciones-flujo-propuesto).
//
// Vive separada del .svelte porque en este repo los componentes Svelte no se
// testean a propósito; acá sí, y es donde están las reglas que se pueden
// equivocar (qué badge va con qué estado, cuándo se puede cancelar, de qué color
// va una barra).
//
// Regla que atraviesa todo el archivo: esta pantalla MUESTRA estado, no vende.
// Nada de precios, comparativas ni "mejorá tu plan".
import { formatFecha } from "$lib/stringutil/lib";

// Umbral del aviso de uso (decisión de negocio #4).
const UMBRAL_ATENCION = 80;

/**
 * El badge de estado: qué texto y de qué color.
 *
 * Ojo con CANCELLED: no dice "Cancelado" sino "Finaliza el ...", porque el
 * acceso sigue vigente hasta el fin del período pagado. Decirle "cancelado" a
 * alguien que todavía tiene 20 días de plan es mentirle.
 */
export function estadoSuscripcion(sub) {
    if (!sub) return { texto: "Sin plan", color: "gray" };

    switch (sub.estado) {
        case "ACTIVE":
            return { texto: "Activo", color: "green" };
        case "PENDING":
            return { texto: "Pago pendiente", color: "yellow" };
        case "PAST_DUE":
            // El acceso NO se cortó: es un aviso, no una baja.
            return { texto: "Problema de cobro", color: "red" };
        case "CANCELLED": {
            const fin = formatFecha(sub.finPeriodo);
            if (fin && vigente(sub)) {
                return { texto: `Finaliza el ${fin}`, color: "yellow" };
            }
            return { texto: "Finalizado", color: "gray" };
        }
        default:
            return { texto: "Sin plan", color: "gray" };
    }
}

/**
 * El badge que corresponde mostrar junto al plan que rige HOY.
 *
 * No es lo mismo que `estadoSuscripcion`: cuando la suscripción paga ya venció,
 * el plan que rige es el Gratuito y ese plan está **activo**. Mostrar ahí
 * "Finalizado" al lado del nombre "Gratuito" se lee como si el Gratuito hubiera
 * terminado, cuando lo que terminó fue el pago.
 *
 * `PENDING` es la excepción: no da acceso, pero avisar que hay un pago en curso
 * es información útil, no ruido.
 */
export function badgeDePlan(sub) {
    if (!sub) return { texto: "Activo", color: "green" };
    if (vigente(sub) || sub.estado === "PENDING") return estadoSuscripcion(sub);
    // La suscripción paga murió: rige el Gratuito, y está activo.
    return { texto: "Activo", color: "green" };
}

/** Si la suscripción todavía da acceso. Espeja `grantsAccess` del backend. */
export function vigente(sub) {
    if (!sub) return false;
    if (sub.estado === "ACTIVE" || sub.estado === "PAST_DUE") return true;
    if (sub.estado === "CANCELLED") {
        return !!sub.finPeriodo && new Date(sub.finPeriodo) > new Date();
    }
    return false;
}

/**
 * La línea bajo el nombre del plan. Un plan gratuito no se renueva ni vence, así
 * que no se le inventa una fecha.
 */
export function leyendaFecha(sub) {
    if (!sub || !sub.finPeriodo) return "";
    // Una suscripción que ya venció no tiene nada que anunciar: su fecha está en
    // el pasado, y "Tu plan termina el 16/08" leído el 17/08 no significa nada.
    if (!vigente(sub)) return "";
    const fecha = formatFecha(sub.finPeriodo);
    if (!fecha) return "";
    if (sub.estado === "CANCELLED") return `Tu plan termina el ${fecha}`;
    return `Se renueva el ${fecha}`;
}

/**
 * Cancelar es gestión de cuenta, no venta: Apple lo permite y lo exige la
 * decencia. Pero solo tiene sentido sobre un plan pago que siga vigente — no
 * sobre el Gratuito ni sobre uno ya cancelado.
 */
export function puedeCancelar(sub) {
    if (!sub) return false;
    if (!sub.nivel || sub.nivel === 0) return false;
    return sub.estado === "ACTIVE" || sub.estado === "PAST_DUE";
}

/**
 * El porcentaje de la barra, o `null` si el plan no tiene tope (Ilimitado).
 *
 * `tope: null` es SIN LÍMITE, no cero — tratarlo como cero pintaría al plan más
 * caro como el más lleno.
 */
export function porcentajeUso(tramo) {
    if (!tramo || tramo.tope === null || tramo.tope === undefined) return null;
    if (tramo.tope <= 0) return null;
    return Math.min(100, Math.round((tramo.usado / tramo.tope) * 100));
}

/**
 * El color de la barra según cuánto falta para el tope: normal hasta 79%,
 * atención de 80 a 99, tope al 100 (§3.3).
 */
export function nivelDeUso(tramo) {
    const pct = porcentajeUso(tramo);
    if (pct === null) return "sin-limite";
    if (pct >= 100) return "tope";
    if (pct >= UMBRAL_ATENCION) return "atencion";
    return "normal";
}

/** El texto a la derecha de la barra: "212 / 250", o "212" si no hay tope. */
export function textoUso(tramo) {
    if (!tramo) return "";
    if (tramo.tope === null || tramo.tope === undefined) return `${tramo.usado}`;
    return `${tramo.usado} / ${tramo.tope}`;
}
